import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, ImagePlus, X, Star, Trash2,
  Calendar, Clock, MapPin, DollarSign, Tag, Link as LinkIcon,
  FileText, Eye, EyeOff, FolderOpen,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import api, { mediaUrl } from '../api';
import MediaPicker from '../components/MediaPicker';
import { toast } from 'sonner';

const blank = {
  title: '',
  start_date: '',
  end_date: '',
  start_time: '',
  end_time: '',
  display_date: '',
  location: '',
  category_id: '',
  description: '',
  short_description: '',
  cost: '',
  featured_image: '',
  register_url: '',
  is_featured: false,
  status: 'published',
};

export default function EventEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const [form, setForm] = useState(blank);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: cats } = await api.get('/event-categories');
        setCategories(cats);
        if (!isNew) {
          const { data } = await api.get(`/events/${id}`);
          setForm({
            title: data.title || '',
            start_date: data.start_date || '',
            end_date: data.end_date || '',
            start_time: data.start_time || '',
            end_time: data.end_time || '',
            display_date: data.display_date || '',
            location: data.location || '',
            category_id: data.category_id || '',
            description: data.description || '',
            short_description: data.short_description || '',
            cost: data.cost || '',
            featured_image: data.featured_image || '',
            register_url: data.register_url || '',
            is_featured: !!data.is_featured,
            status: data.status || 'published',
          });
        }
      } catch (e) {
        toast.error('Failed to load event');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/media/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update('featured_image', data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.start_date) {
      toast.error('Title and start date are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, category_id: form.category_id || null };
      if (isNew) {
        const { data } = await api.post('/events', payload);
        toast.success('Event created');
        navigate(`/admin/events/${data.id}`, { replace: true });
      } else {
        await api.put(`/events/${id}`, payload);
        toast.success('Saved');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete event "${form.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/events/${id}`);
      toast.success('Event deleted');
      navigate('/admin/events');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === form.category_id),
    [categories, form.category_id]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-white/50">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  const imgUrl = form.featured_image ? mediaUrl(form.featured_image) : '';

  return (
    <div className="max-w-6xl">
      {/* Top bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap mb-7">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/events" className="h-9 w-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">{isNew ? 'New Event' : 'Edit Event'}</div>
            <div className="font-serif-display text-[24px] font-bold truncate">{form.title || 'Untitled event'}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button onClick={handleDelete} variant="ghost" className="h-10 rounded-full text-white/55 hover:text-red-400 hover:bg-red-500/10 font-semibold">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving} className="h-10 rounded-full bg-[#E5614C] hover:bg-[#d04b36] text-white font-semibold px-5 btn-press shadow-[0_8px_20px_-6px_rgba(229,97,76,0.5)]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1.5" /> {isNew ? 'Create Event' : 'Save Changes'}</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <Field label="Event Title *">
              <Input value={form.title} onChange={(e) => update('title', e.target.value)} placeholder="e.g. Summer Kickoff Concert" className={inputCls} />
            </Field>
            <Field label="Short Description">
              <Input value={form.short_description} onChange={(e) => update('short_description', e.target.value)} placeholder="One-line teaser (shown on cards)" className={inputCls} />
            </Field>
            <Field label="Full Description">
              <Textarea rows={6} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Tell the full story of the event…" className={textareaCls} />
            </Field>
          </Card>

          <Card title="Date & Time" icon={Calendar}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Start Date *">
                <Input type="date" value={form.start_date} onChange={(e) => update('start_date', e.target.value)} className={inputCls} />
              </Field>
              <Field label="End Date (optional)">
                <Input type="date" value={form.end_date || ''} onChange={(e) => update('end_date', e.target.value)} className={inputCls} />
              </Field>
              <Field label="Start Time">
                <Input value={form.start_time} onChange={(e) => update('start_time', e.target.value)} placeholder="e.g. 6:00 PM" className={inputCls} />
              </Field>
              <Field label="End Time">
                <Input value={form.end_time} onChange={(e) => update('end_time', e.target.value)} placeholder="e.g. 9:00 PM" className={inputCls} />
              </Field>
            </div>
            <Field label="Display Date Override (optional)">
              <Input value={form.display_date} onChange={(e) => update('display_date', e.target.value)} placeholder='e.g. "Mondays", "Daily", "Every Saturday"' className={inputCls} />
              <div className="text-[11px] text-white/40 mt-1">For recurring events. If set, this text replaces the auto-formatted date on public cards.</div>
            </Field>
          </Card>

          <Card title="Location & Details" icon={MapPin}>
            <Field label="Location">
              <Input value={form.location} onChange={(e) => update('location', e.target.value)} placeholder="e.g. 160 Hollis St, Framingham, MA" className={inputCls} />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Cost">
                <div className="relative">
                  <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input value={form.cost} onChange={(e) => update('cost', e.target.value)} placeholder="Free / $10 / Suggested donation $5" className={`${inputCls} pl-9`} />
                </div>
              </Field>
              <Field label="Registration URL">
                <div className="relative">
                  <LinkIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                  <Input value={form.register_url} onChange={(e) => update('register_url', e.target.value)} placeholder="https://…" className={`${inputCls} pl-9`} />
                </div>
              </Field>
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <Card title="Publish" icon={Eye}>
            <Field label="Status">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button" onClick={() => update('status', 'published')}
                  className={`h-10 rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                    form.status === 'published'
                      ? 'bg-[#5DCAB5]/15 border-[#5DCAB5]/40 text-[#5DCAB5]'
                      : 'bg-[#0f0d12] border-white/10 text-white/55 hover:text-white'
                  }`}
                ><Eye className="h-3.5 w-3.5" /> Published</button>
                <button
                  type="button" onClick={() => update('status', 'draft')}
                  className={`h-10 rounded-lg border text-[13px] font-semibold inline-flex items-center justify-center gap-1.5 transition-colors ${
                    form.status === 'draft'
                      ? 'bg-white/10 border-white/20 text-white'
                      : 'bg-[#0f0d12] border-white/10 text-white/55 hover:text-white'
                  }`}
                ><EyeOff className="h-3.5 w-3.5" /> Draft</button>
              </div>
            </Field>
            <div className="flex items-center justify-between py-2">
              <div>
                <div className="text-[13px] font-semibold text-white inline-flex items-center gap-1.5">
                  <Star className="h-3.5 w-3.5 text-[#FCD968]" fill={form.is_featured ? '#FCD968' : 'transparent'} /> Featured event
                </div>
                <div className="text-[11px] text-white/45 mt-0.5">Highlight on the homepage</div>
              </div>
              <Switch checked={form.is_featured} onCheckedChange={(v) => update('is_featured', v)} />
            </div>
          </Card>

          <Card title="Category" icon={Tag}>
            <Field label="Choose a category">
              <select
                value={form.category_id || ''} onChange={(e) => update('category_id', e.target.value)}
                className="w-full h-11 rounded-lg bg-[#0f0d12] border border-white/10 text-white text-[14px] px-3 focus:outline-none focus:ring-2 focus:ring-[#E5614C]/40"
              >
                <option value="">— None —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
            {selectedCategory && (
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em] border ${selectedCategory.color === 'coral' ? 'bg-[#E5614C]/15 text-[#E5614C] border-[#E5614C]/30' : selectedCategory.color === 'teal' ? 'bg-[#5DCAB5]/15 text-[#5DCAB5] border-[#5DCAB5]/30' : 'bg-[#FCD968]/15 text-[#FCD968] border-[#FCD968]/30'}`}>
                {selectedCategory.name}
              </div>
            )}
          </Card>

          <Card title="Featured Image" icon={ImagePlus}>
            {imgUrl ? (
              <div className="relative rounded-xl overflow-hidden bg-[#0f0d12] border border-white/[0.06] aspect-[16/10] group">
                <img src={imgUrl} alt="featured" className="w-full h-full object-cover" />
                <button onClick={() => update('featured_image', '')} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/60 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" title="Remove">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <div className="aspect-[16/10] rounded-xl border-2 border-dashed border-white/10 flex flex-col items-center justify-center text-white/40 text-[12px]">
                <ImagePlus className="h-6 w-6 mb-1" />
                No image yet
              </div>
            )}
            <Button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
              className="mt-3 w-full h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ImagePlus className="h-4 w-4 mr-1.5" /> Upload Image</>}
            </Button>
            <Button type="button" onClick={() => setPickerOpen(true)}
              className="mt-2 w-full h-10 rounded-lg bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10">
              <FolderOpen className="h-4 w-4 mr-1.5" /> Choose from Library
            </Button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
            <Input
              value={form.featured_image || ''} onChange={(e) => update('featured_image', e.target.value)}
              placeholder="Or paste an image URL"
              className={`${inputCls} mt-2 text-[12px]`}
            />
            <MediaPicker
              open={pickerOpen}
              onClose={() => setPickerOpen(false)}
              onSelect={(item) => update('featured_image', item.url)}
              title="Pick a featured image"
            />
          </Card>
        </div>
      </div>
    </div>
  );
}

const inputCls = 'h-11 rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C]';
const textareaCls = 'rounded-lg bg-[#0f0d12] border-white/10 text-white text-[14px] placeholder:text-white/30 focus-visible:ring-[#E5614C]/40 focus-visible:border-[#E5614C] resize-none leading-relaxed';

function Card({ title, icon: Icon, children }) {
  return (
    <div className="rounded-2xl bg-[#16131a] border border-white/[0.06] p-5 space-y-4">
      {title && (
        <div className="flex items-center gap-2 text-white/80">
          {Icon && <Icon className="h-4 w-4 text-[#E5614C]" />}
          <div className="font-semibold text-[13px] uppercase tracking-[0.14em]">{title}</div>
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-white/75 text-[12px] font-semibold uppercase tracking-[0.12em]">{label}</Label>
      {children}
    </div>
  );
}
